import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { capitalize } from "@common/helpers/string";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { getServiceTemplateScheduleDisplayStatus } from "../serviceTemplateScheduleDisplayStatus";
import { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    ServiceTemplateScheduleCallbacks,
    ServiceTemplateScheduleTableOptions,
} from "../ServiceTemplateSchedulesList";
import styles from "./ServiceTemplateScheduleStatusCell.module.scss";

export const ServiceTemplateScheduleStatusCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleRow,
        ServiceTemplateRecurrenceStatus
    >) => {
        const meta = useViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >(table);

        // Group rows represent multiple schedules at once (which may have a
        // mix of active/paused/archived/deleted members), so there's no
        // single status to show — the expanded leaf rows below show each
        // schedule's own status.
        if (row.original.rowType === "group") {
            return <ViewTableCell />;
        }

        const { status, templateId, entityUuid } = row.original;
        const isTemplateArchived =
            meta.options?.archivedTemplateIds?.has(templateId) ?? false;
        // Only treated as deleted once the org's workspaces have loaded at
        // least once — otherwise `entityInfoByUuid` is transiently empty on
        // first render and every schedule would flash as "deleted".
        const isClientDeleted =
            (meta.options?.isWorkspacesFetched ?? false) &&
            !meta.options?.entityInfoByUuid?.has(entityUuid);
        const displayStatus = getServiceTemplateScheduleDisplayStatus({
            status,
            isTemplateArchived,
            isClientDeleted,
        });

        const statusText = (
            <span className={styles.statusText}>
                {capitalize(displayStatus)}
            </span>
        );

        // Archived is inferred from the template being archived (never
        // persisted on the schedule itself), and deleted is inferred from the
        // schedule's client no longer being assigned to this organization —
        // neither is obvious from the label alone, so explain the "why" on
        // hover (see PRO-10571).
        if (displayStatus === "archived") {
            return (
                <ViewTableCell>
                    <Tooltip content="This schedule is archived because its template has been archived.">
                        <span>{statusText}</span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        if (displayStatus === "deleted") {
            return (
                <ViewTableCell>
                    <Tooltip content="This schedule's client is no longer part of your organization.">
                        <span>{statusText}</span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        return <ViewTableCell>{statusText}</ViewTableCell>;
    },
);

ServiceTemplateScheduleStatusCell.displayName =
    "ServiceTemplateScheduleStatusCell";
